import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";

import { browserSupabase } from "./supabase";

interface SessionSummary {
  mfa: {
    aal2: boolean;
    compliant: boolean;
    requiredFactors: number;
    verifiedFactors: number;
  };
  role: "accountant" | "authorized_user" | "owner" | "tenant";
  scopes: Array<{
    canManageAuthorizedUsers: boolean;
    propertyId: string | null;
    role: string;
    tenancyId: string | null;
  }>;
  userId: string;
}

interface DashboardData {
  actionItems?: Array<Record<string, unknown>>;
  approvals?: Array<Record<string, unknown>>;
  backupExports?: Array<Record<string, unknown>>;
  costForecasts?: Array<Record<string, unknown>>;
  communicationTimings?: Array<Record<string, unknown>>;
  communications?: Array<Record<string, unknown>>;
  deliveryAttempts?: Array<Record<string, unknown>>;
  documents?: Array<Record<string, unknown>>;
  escalations?: Array<Record<string, unknown>>;
  leases?: Array<Record<string, unknown>>;
  leaseTermVersions?: Array<Record<string, unknown>>;
  ledgerEntries?: Array<Record<string, unknown>>;
  maintenanceCases?: Array<Record<string, unknown>>;
  payments?: Array<Record<string, unknown>>;
  paymentPackets?: Array<Record<string, unknown>>;
  properties?: Array<Record<string, unknown>>;
  providerHealth?: Array<Record<string, unknown>>;
  receipts?: Array<Record<string, unknown>>;
  roleGrants?: Array<Record<string, unknown>>;
  role: SessionSummary["role"];
  restoreDrills?: Array<Record<string, unknown>>;
  tenancies?: Array<Record<string, unknown>>;
  units?: Array<Record<string, unknown>>;
  vendors?: Array<Record<string, unknown>>;
  workOrders?: Array<Record<string, unknown>>;
}

function roleBoundary(role: DashboardData["role"]): { heading: string; summary: string } {
  if (role === "owner") {
    return {
      heading: "Owner permissions",
      summary:
        "You have full operational access. Permanent deletion is not available in this application, and another user cannot remove or reduce the owner account.",
    };
  }
  if (role === "authorized_user") {
    return {
      heading: "Authorized-user permissions",
      summary:
        "You can perform operational work within your assigned scope. You cannot permanently delete records or remove, demote, or reduce the owner account.",
    };
  }
  if (role === "accountant") {
    return {
      heading: "Accountant permissions",
      summary:
        "This workspace is limited to authorized financial records, reconciliation, category corrections, exports, and supporting documents. Tenant communications, vendor dispatch, user management, and deletion are unavailable.",
    };
  }
  return {
    heading: "Tenant permissions",
    summary:
      "This workspace is limited to your own tenancy, tenant-facing records, and maintenance cases. Payment verification and internal operational records are unavailable.",
  };
}

function displayValue(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" || typeof value === "number") {
      if (typeof value === "string" && key.endsWith("_at")) {
        const timestamp = new Date(value);
        if (!Number.isNaN(timestamp.getTime())) {
          return new Intl.DateTimeFormat("en-US", {
            dateStyle: "medium",
            timeStyle: "short",
            timeZone: "America/Los_Angeles",
          }).format(timestamp);
        }
      }
      return String(value).replaceAll("_", " ");
    }
  }
  return "Record";
}

function recordDetail(record: Record<string, unknown>): string {
  const amountCents = record["amount_cents"];
  const ordinaryDetail = displayValue(record, [
    "status",
    "urgency",
    "issued_at",
    "received_at",
    "effective_on",
    "revoked_at",
    "created_at",
  ]);
  if (typeof amountCents !== "number") {
    return ordinaryDetail;
  }
  const amount = new Intl.NumberFormat("en-US", {
    currency: "USD",
    style: "currency",
  }).format(amountCents / 100);
  return `${amount} · ${ordinaryDetail}`;
}

function scalarValue(value: unknown, fallback = "missing"): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : fallback;
}

function centsFromDollars(value: string): number | null {
  if (value.trim() === "") {
    return 0;
  }
  if (!/^(?:0|[1-9][0-9]*)(?:\.[0-9]{1,2})?$/u.test(value.trim())) {
    return null;
  }
  const cents = Math.round(Number(value) * 100);
  return Number.isSafeInteger(cents) && cents >= 0 ? cents : null;
}

function safeDownloadUrl(value: unknown): string | null {
  if (typeof value !== "string" || value.length === 0) {
    return null;
  }
  try {
    const parsed = new URL(value);
    const localHttp =
      parsed.protocol === "http:" &&
      (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost");
    return parsed.protocol === "https:" || localHttp ? parsed.href : null;
  } catch {
    return null;
  }
}

async function accessToken(): Promise<string | null> {
  const client = browserSupabase();
  if (client === null) {
    return null;
  }
  const { data } = await client.auth.getSession();
  return data.session?.access_token ?? null;
}

async function apiRequest(path: string, init?: RequestInit): Promise<Response> {
  try {
    const token = await accessToken();
    if (token === null) {
      return new Response(null, { status: 401 });
    }
    const headers = new Headers(init?.headers);
    headers.set("Authorization", `Bearer ${token}`);
    return await fetch(path, { ...init, headers });
  } catch {
    return new Response(null, { status: 503 });
  }
}

export function SignIn() {
  const client = browserSupabase();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [status, setStatus] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (client === null) {
      return;
    }
    setSubmitting(true);
    setStatus("Signing in…");
    const { error } = await client.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error !== null) {
      setStatus("Sign-in was not accepted. Check your information or contact the owner.");
      return;
    }
    void navigate("/portal");
  }

  return (
    <section className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Private application</p>
        <h1>Sign in</h1>
        <p>Access is limited to the owner, authorized users, accountant, and current tenants.</p>
        <form onSubmit={(event) => void submit(event)}>
          <label htmlFor="email">Email address</label>
          <input
            autoComplete="email"
            disabled={client === null || submitting}
            id="email"
            name="email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
          <label htmlFor="password">Password</label>
          <input
            autoComplete="current-password"
            disabled={client === null || submitting}
            id="password"
            name="password"
            onChange={(event) => setPassword(event.target.value)}
            required
            type="password"
            value={password}
          />
          <button className="button" disabled={client === null || submitting} type="submit">
            Continue securely
          </button>
        </form>
        <p className="form-note" role="status">
          {client === null
            ? "Identity service is disabled in this private preview. No credentials are accepted."
            : status || "Multi-factor verification is required after your password."}
        </p>
      </div>
    </section>
  );
}

export function MfaVerification() {
  const client = browserSupabase();
  const navigate = useNavigate();
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [phone, setPhone] = useState("");
  const [verifiedPhoneFactorId, setVerifiedPhoneFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [status, setStatus] = useState("Checking your security factors…");

  const prepare = useCallback(
    async (enrollAnother = false) => {
      if (client === null) {
        setStatus("Identity service is not configured.");
        return;
      }
      const listed = await client.auth.mfa.listFactors();
      if (listed.error !== null) {
        setStatus("Security factors could not be loaded.");
        return;
      }
      const verifiedTotp = listed.data.totp.find((factor) => factor.status === "verified");
      const verifiedPhone = listed.data.phone.find((factor) => factor.status === "verified");
      setVerifiedPhoneFactorId(verifiedPhone?.id ?? null);
      const verified = verifiedTotp ?? verifiedPhone;
      if (verified !== undefined && !enrollAnother) {
        setFactorId(verified.id);
        setQrCode(null);
        if (verifiedPhone?.id === verified.id) {
          const challenge = await client.auth.mfa.challenge({ factorId: verified.id });
          if (challenge.error !== null) {
            setStatus("A phone verification code could not be sent.");
            return;
          }
          setChallengeId(challenge.data.id);
          setStatus("A verification code was sent to your enrolled phone.");
        } else {
          setChallengeId(null);
          setStatus("Enter the current code from your authenticator app.");
        }
        return;
      }
      const enrolled = await client.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `Potimus authenticator ${listed.data.all.length + 1}`,
      });
      if (enrolled.error !== null) {
        setStatus("A new authenticator could not be enrolled.");
        return;
      }
      setFactorId(enrolled.data.id);
      setChallengeId(null);
      setQrCode(enrolled.data.totp.qr_code);
      setStatus("Scan this code in an authenticator app, then enter its six-digit code.");
    },
    [client],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => void prepare(), 0);
    return () => window.clearTimeout(timeout);
  }, [prepare]);

  async function verify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (client === null || factorId === null) {
      return;
    }
    setStatus("Verifying…");
    const result =
      challengeId === null
        ? await client.auth.mfa.challengeAndVerify({ code, factorId })
        : await client.auth.mfa.verify({ challengeId, code, factorId });
    if (result.error !== null) {
      setStatus("That code was not accepted. Try the next current code.");
      return;
    }
    void navigate("/portal");
  }

  async function enrollPhone(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (client === null || !/^\+[1-9][0-9]{7,14}$/u.test(phone)) {
      setStatus("Enter a mobile number in international format, such as +15035550123.");
      return;
    }
    setStatus("Enrolling the SMS factor…");
    const enrolled = await client.auth.mfa.enroll({
      factorType: "phone",
      friendlyName: "Potimus owner SMS factor",
      phone,
    });
    if (enrolled.error !== null) {
      setStatus("The SMS factor could not be enrolled. Confirm phone MFA is enabled.");
      return;
    }
    const challenge = await client.auth.mfa.challenge({ factorId: enrolled.data.id });
    if (challenge.error !== null) {
      setStatus("The SMS factor was created, but a verification code could not be sent.");
      return;
    }
    setCode("");
    setFactorId(enrolled.data.id);
    setChallengeId(challenge.data.id);
    setQrCode(null);
    setStatus("A verification code was sent. Enter it above to activate the SMS factor.");
  }

  async function challengeVerifiedPhone() {
    if (client === null || verifiedPhoneFactorId === null) {
      return;
    }
    setStatus("Sending a code to your enrolled phone…");
    const challenge = await client.auth.mfa.challenge({ factorId: verifiedPhoneFactorId });
    if (challenge.error !== null) {
      setStatus("A phone verification code could not be sent.");
      return;
    }
    setCode("");
    setFactorId(verifiedPhoneFactorId);
    setChallengeId(challenge.data.id);
    setQrCode(null);
    setStatus("A verification code was sent to your enrolled phone.");
  }

  return (
    <section className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Account security</p>
        <h1>Verify it’s you</h1>
        <p aria-live="polite" role="status">
          {status}
        </p>
        {qrCode === null ? null : (
          <img alt="Authenticator enrollment QR code" className="mfa-qr" src={qrCode} />
        )}
        <form onSubmit={(event) => void verify(event)}>
          <label htmlFor="verification-code">Six-digit code</label>
          <input
            autoComplete="one-time-code"
            disabled={factorId === null}
            id="verification-code"
            inputMode="numeric"
            maxLength={8}
            onChange={(event) => setCode(event.target.value.replace(/\D/gu, ""))}
            required
            value={code}
          />
          <button className="button" disabled={factorId === null || code.length < 6} type="submit">
            Verify
          </button>
        </form>
        <button className="text-button" onClick={() => void prepare(true)} type="button">
          Enroll another authenticator app
        </button>
        {verifiedPhoneFactorId === null ? null : (
          <button
            className="text-button"
            onClick={() => void challengeVerifiedPhone()}
            type="button"
          >
            Use my enrolled phone instead
          </button>
        )}
        <form onSubmit={(event) => void enrollPhone(event)}>
          <label htmlFor="mfa-phone">Mobile number for a phone factor</label>
          <input
            aria-describedby="mfa-factor-guidance"
            autoComplete="tel"
            id="mfa-phone"
            onChange={(event) => setPhone(event.target.value.trim())}
            placeholder="+15035550123"
            required
            type="tel"
            value={phone}
          />
          <button className="button button-small" type="submit">
            Enroll SMS factor
          </button>
        </form>
        <p className="form-note" id="mfa-factor-guidance">
          A phone factor is independent only when it is controlled separately from the authenticator
          device. The owner account must maintain two independently controlled factors. Other
          accounts require at least one.
        </p>
      </div>
    </section>
  );
}

function RecordSection(props: {
  empty: string;
  records: Array<Record<string, unknown>> | undefined;
  title: string;
}) {
  return (
    <section className="portal-panel">
      <h2>{props.title}</h2>
      {props.records === undefined || props.records.length === 0 ? (
        <p>{props.empty}</p>
      ) : (
        <ul className="record-list">
          {props.records.map((record, index) => (
            <li key={typeof record["id"] === "string" ? record["id"] : `${props.title}-${index}`}>
              <strong>
                {displayValue(record, [
                  "title",
                  "legal_name",
                  "receipt_number",
                  "case_number",
                  "payer_name",
                  "classification",
                  "channel",
                  "role",
                  "kind",
                ])}
              </strong>
              <span>{recordDetail(record)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function documentKindLabel(kind: unknown): string {
  if (kind === "signed_lease") {
    return "Signed lease";
  }
  if (kind === "receipt") {
    return "Payment receipt";
  }
  if (kind === "tenant_notice") {
    return "Tenant-facing notice";
  }
  if (kind === "vendor_invoice") {
    return "Vendor invoice";
  }
  return "Document";
}

function DocumentSection(props: {
  documents: Array<Record<string, unknown>> | undefined;
  empty: string;
  onDownload: (documentId: string) => Promise<void>;
  pendingAction: string | null;
  title: string;
}) {
  return (
    <section className="portal-panel">
      <h2>{props.title}</h2>
      <p className="form-note">Secure download links expire after 60 seconds.</p>
      {props.documents?.length ? (
        <ul className="record-list">
          {props.documents.map((document) => {
            const documentId = String(document["id"]);
            const action = `document:${documentId}`;
            const pending = props.pendingAction === action;
            const title = displayValue(document, ["title", "kind"]);
            return (
              <li aria-busy={pending} key={documentId}>
                <span className="record-copy">
                  <strong>{title}</strong>
                  <span>{documentKindLabel(document["kind"])}</span>
                </span>
                <button
                  aria-label={`Secure download ${title}`}
                  className="text-button"
                  disabled={props.pendingAction !== null}
                  onClick={() => void props.onDownload(documentId)}
                  type="button"
                >
                  {pending ? "Preparing secure download…" : "Secure download"}
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p>{props.empty}</p>
      )}
    </section>
  );
}

function elapsedSeconds(later: unknown, earlier: unknown): number | null {
  if (typeof later !== "string" || typeof earlier !== "string") {
    return null;
  }
  const difference = Date.parse(later) - Date.parse(earlier);
  return Number.isFinite(difference) && difference >= 0 ? Math.round(difference / 100) / 10 : null;
}

function CommunicationTimingSection(props: {
  attempts: Array<Record<string, unknown>> | undefined;
  communications: Array<Record<string, unknown>> | undefined;
}) {
  if (props.communications === undefined || props.communications.length === 0) {
    return (
      <section className="portal-panel portal-panel-wide">
        <h2>Communication timing</h2>
        <p>No outbound acknowledgment timings are recorded.</p>
      </section>
    );
  }
  return (
    <section className="portal-panel portal-panel-wide">
      <h2>Communication timing</h2>
      <p>
        System acknowledgment is measured from receipt of the valid provider webhook. Provider
        delivery is shown separately so carrier or mailbox delays remain visible.
      </p>
      <ul className="record-list">
        {props.communications.map((communication) => {
          const attempt = props.attempts?.find(
            (candidate) => candidate["communication_id"] === communication["id"],
          );
          const acknowledgmentSeconds = elapsedSeconds(
            communication["acknowledged_at"],
            communication["system_received_at"],
          );
          const providerDeliverySeconds = elapsedSeconds(
            attempt?.["provider_delivered_at"],
            attempt?.["provider_accepted_at"],
          );
          return (
            <li key={String(communication["id"])}>
              <strong>{displayValue(communication, ["classification", "channel"])}</strong>
              <span>
                System acknowledgment: {acknowledgmentSeconds ?? "pending"}
                {acknowledgmentSeconds === null ? "" : " seconds"}; provider delivery:{" "}
                {providerDeliverySeconds ?? scalarValue(attempt?.["status"], "pending")}
                {providerDeliverySeconds === null ? "" : " seconds"}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function Portal() {
  const client = browserSupabase();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [status, setStatus] = useState("Loading your authorized records…");
  const [description, setDescription] = useState("");
  const [preferredChannel, setPreferredChannel] = useState<"email" | "sms">("email");
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [maintenanceMediaCaseId, setMaintenanceMediaCaseId] = useState("");
  const [maintenanceMediaFile, setMaintenanceMediaFile] = useState<File | null>(null);
  const [documentKind, setDocumentKind] = useState<"other" | "signed_lease" | "vendor_invoice">(
    "signed_lease",
  );
  const [documentPropertyId, setDocumentPropertyId] = useState("");
  const [documentTenancyId, setDocumentTenancyId] = useState("");
  const [documentTitle, setDocumentTitle] = useState("");
  const [ledgerAccountCode, setLedgerAccountCode] = useState("");
  const [ledgerCategory, setLedgerCategory] = useState("");
  const [ledgerEntryId, setLedgerEntryId] = useState("");
  const [ledgerStatus, setLedgerStatus] = useState<"exception" | "matched" | "reviewed">("matched");
  const [paymentFirstMonth, setPaymentFirstMonth] = useState("");
  const [paymentLastMonth, setPaymentLastMonth] = useState("");
  const [paymentPayer, setPaymentPayer] = useState("");
  const [paymentReceivedAt, setPaymentReceivedAt] = useState("");
  const [paymentRent, setPaymentRent] = useState("");
  const [paymentRentPeriodEnd, setPaymentRentPeriodEnd] = useState("");
  const [paymentRentPeriodStart, setPaymentRentPeriodStart] = useState("");
  const [paymentSecurityDeposit, setPaymentSecurityDeposit] = useState("");
  const [paymentTenancyId, setPaymentTenancyId] = useState("");
  const [userGrantAction, setUserGrantAction] = useState<"grant" | "revoke">("grant");
  const [userGrantCanManage, setUserGrantCanManage] = useState(false);
  const [userGrantPropertyId, setUserGrantPropertyId] = useState("");
  const [userGrantTargetId, setUserGrantTargetId] = useState("");
  const [title, setTitle] = useState("");
  const [urgency, setUrgency] = useState<"emergency" | "routine" | "urgent">("routine");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sessionResponse = await apiRequest("/api/session");
      if (sessionResponse.status === 401) {
        void navigate("/sign-in");
        return;
      }
      if (!sessionResponse.ok) {
        setDashboard(null);
        setStatus("Your authorized session could not be loaded.");
        return;
      }
      const sessionData = (await sessionResponse.json()) as SessionSummary;
      setSession(sessionData);
      if (!sessionData.mfa.compliant) {
        setDashboard(null);
        setStatus("Multi-factor verification is required before records can be shown.");
        return;
      }
      const dashboardResponse = await apiRequest("/api/dashboard");
      if (!dashboardResponse.ok) {
        setDashboard(null);
        setStatus("Your records are temporarily unavailable.");
        return;
      }
      const dashboardData = (await dashboardResponse.json()) as DashboardData;
      if (dashboardData.role !== sessionData.role) {
        setDashboard(null);
        setStatus("Your authorization scope could not be verified. No records were shown.");
        return;
      }
      setDashboard(dashboardData);
      const savedPreference = dashboardData.tenancies?.[0]?.["preferred_channel"];
      if (savedPreference === "email" || savedPreference === "sms") {
        setPreferredChannel(savedPreference);
      }
      setStatus("");
    } catch {
      setDashboard(null);
      setStatus("Your authorized records could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const timeout = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timeout);
  }, [load]);

  async function signOut() {
    await client?.auth.signOut();
    void navigate("/sign-in");
  }

  async function submitMaintenance(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const tenancy = dashboard?.tenancies?.[0];
    const propertyId = tenancy?.["property_id"];
    const tenancyId = tenancy?.["id"];
    const unitId = tenancy?.["unit_id"];
    if (
      typeof propertyId !== "string" ||
      typeof tenancyId !== "string" ||
      typeof unitId !== "string"
    ) {
      setStatus("A current tenancy is required to submit maintenance.");
      return;
    }
    setStatus("Submitting your maintenance request…");
    const response = await apiRequest("/api/maintenance", {
      body: JSON.stringify({ description, propertyId, tenancyId, title, unitId, urgency }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The request was not accepted. Please call or text management if this continues.");
      return;
    }
    setDescription("");
    setTitle("");
    setStatus("Your request was received and is being followed through.");
    await load();
  }

  async function saveCommunicationPreference(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (preferredChannel === "sms") {
      setStatus(
        "Text messaging requires separate recorded consent and activation. This preference form does not opt you in.",
      );
      return;
    }
    const tenancyId = dashboard?.tenancies?.[0]?.["id"];
    if (typeof tenancyId !== "string") {
      setStatus("A current tenancy is required to save a communication preference.");
      return;
    }
    setStatus("Saving your communication preference…");
    const response = await apiRequest("/api/tenancy/communication-preference", {
      body: JSON.stringify({ preferredChannel, tenancyId }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The preference could not be saved. Your existing preference remains in effect.");
      return;
    }
    setStatus("Your routine communication preference was saved.");
    await load();
  }

  async function downloadDocument(documentId: string) {
    const action = `document:${documentId}`;
    if (pendingAction !== null) {
      return;
    }
    setPendingAction(action);
    setStatus("Preparing a short-lived secure download link…");
    try {
      const response = await apiRequest(
        `/api/documents/${encodeURIComponent(documentId)}/download`,
      );
      if (!response.ok) {
        setStatus("A secure download link could not be created. Please try again.");
        return;
      }
      const result = (await response.json()) as { url?: unknown };
      const url = safeDownloadUrl(result.url);
      if (url === null) {
        setStatus("The secure download response was invalid. Please try again.");
        return;
      }
      setStatus("Secure download ready. The link expires shortly.");
      window.location.assign(url);
    } catch {
      setStatus("A secure download link could not be created. Please try again.");
    } finally {
      setPendingAction(null);
    }
  }

  async function confirmClosure(caseId: string) {
    setStatus("Recording your completion confirmation…");
    const response = await apiRequest(
      `/api/maintenance/${encodeURIComponent(caseId)}/confirm-closure`,
      {
        body: JSON.stringify({ summary: "Tenant confirmed that the reported work is complete." }),
        headers: {
          "Content-Type": "application/json",
          "x-idempotency-key": crypto.randomUUID(),
        },
        method: "POST",
      },
    );
    if (!response.ok) {
      setStatus("Completion could not be confirmed. Management will continue following the case.");
      return;
    }
    setStatus("Completion was confirmed and recorded.");
    await load();
  }

  async function uploadMaintenanceMedia(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const tenancy = dashboard?.tenancies?.[0];
    const propertyId = tenancy?.["property_id"];
    const tenancyId = tenancy?.["id"];
    if (
      maintenanceMediaFile === null ||
      maintenanceMediaCaseId === "" ||
      typeof propertyId !== "string" ||
      typeof tenancyId !== "string"
    ) {
      setStatus("Select a maintenance case and a photo or short video.");
      return;
    }
    const form = new FormData();
    form.set("audience", "internal");
    form.set("file", maintenanceMediaFile);
    form.set("kind", "maintenance_media");
    form.set("maintenanceCaseId", maintenanceMediaCaseId);
    form.set("propertyId", propertyId);
    form.set("tenancyId", tenancyId);
    form.set("title", `Tenant maintenance media - ${maintenanceMediaFile.name}`);
    setStatus("Saving the private maintenance attachment…");
    const response = await apiRequest("/api/documents", {
      body: form,
      headers: { "x-idempotency-key": crypto.randomUUID() },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The attachment was not accepted. Your maintenance case remains open.");
      return;
    }
    setMaintenanceMediaFile(null);
    setStatus("The attachment was saved privately with the maintenance case.");
  }

  async function downloadAccountingExport() {
    if (pendingAction !== null) {
      return;
    }
    setPendingAction("accounting:csv");
    setStatus("Preparing the authorized accounting export…");
    try {
      const response = await apiRequest("/api/accounting/export.csv");
      if (!response.ok) {
        setStatus("The accounting export is temporarily unavailable.");
        return;
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `potimus-accounting-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(blobUrl);
      setStatus("Accounting export downloaded.");
    } catch {
      setStatus("The accounting export is temporarily unavailable.");
    } finally {
      setPendingAction(null);
    }
  }

  async function correctLedgerCategory(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (ledgerEntryId === "" || ledgerCategory.trim() === "" || ledgerAccountCode.trim() === "") {
      setStatus("Select a ledger entry and enter its category and account code.");
      return;
    }
    setStatus("Recording the audited category correction…");
    const response = await apiRequest(`/api/ledger/${encodeURIComponent(ledgerEntryId)}/category`, {
      body: JSON.stringify({ accountCode: ledgerAccountCode, category: ledgerCategory }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The ledger category was not changed.");
      return;
    }
    setStatus("The category correction was recorded with an audit event.");
    await load();
  }

  async function confirmManualPayment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const tenancy = dashboard?.tenancies?.find((candidate) => candidate["id"] === paymentTenancyId);
    const propertyId = tenancy?.["property_id"];
    const receivedAt = new Date(paymentReceivedAt);
    const values = {
      firstMonth: centsFromDollars(paymentFirstMonth),
      lastMonth: centsFromDollars(paymentLastMonth),
      rent: centsFromDollars(paymentRent),
      securityDeposit: centsFromDollars(paymentSecurityDeposit),
    };
    if (
      typeof propertyId !== "string" ||
      paymentPayer.trim() === "" ||
      Number.isNaN(receivedAt.getTime()) ||
      Object.values(values).some((value) => value === null)
    ) {
      setStatus("Enter a tenancy, payer, receipt time, and valid dollar allocations.");
      return;
    }
    const lease = dashboard?.leases?.find(
      (candidate) =>
        candidate["tenancy_id"] === paymentTenancyId && candidate["status"] === "confirmed",
    );
    const confirmedVersionId = lease?.["confirmed_version_id"];
    const confirmedTerms = dashboard?.leaseTermVersions?.find(
      (candidate) => candidate["id"] === confirmedVersionId,
    );
    const initialPaymentCents =
      (values.securityDeposit ?? 0) + (values.firstMonth ?? 0) + (values.lastMonth ?? 0);
    if (initialPaymentCents > 0 && typeof confirmedVersionId !== "string") {
      setStatus("Deposit, first-month, and last-month allocations require confirmed lease terms.");
      return;
    }
    const initialMatches = [
      [values.securityDeposit ?? 0, confirmedTerms?.["security_deposit_cents"]],
      [values.firstMonth ?? 0, confirmedTerms?.["rent_cents"]],
      [values.lastMonth ?? 0, confirmedTerms?.["prepaid_last_month_cents"]],
    ].every(([entered, expected]) => entered === 0 || entered === expected);
    if (!initialMatches) {
      setStatus("Deposit, first-month, and last-month amounts must match confirmed lease terms.");
      return;
    }
    if ((values.rent ?? 0) > 0 && (paymentRentPeriodStart === "" || paymentRentPeriodEnd === "")) {
      setStatus("A rent allocation requires the covered period start and end dates.");
      return;
    }
    const allocations = [
      {
        amountCents: values.securityDeposit ?? 0,
        category: "security_deposit" as const,
        confirmedLeaseTermVersionId: confirmedVersionId,
      },
      {
        amountCents: values.firstMonth ?? 0,
        category: "first_month_rent" as const,
        confirmedLeaseTermVersionId: confirmedVersionId,
      },
      {
        amountCents: values.lastMonth ?? 0,
        category: "last_month_prepaid_rent" as const,
        confirmedLeaseTermVersionId: confirmedVersionId,
      },
      {
        amountCents: values.rent ?? 0,
        category: "rent" as const,
        periodEnd: paymentRentPeriodEnd || null,
        periodStart: paymentRentPeriodStart || null,
      },
    ].filter((allocation) => allocation.amountCents > 0);
    const amountCents = allocations.reduce(
      (total, allocation) => total + allocation.amountCents,
      0,
    );
    if (amountCents <= 0) {
      setStatus("Enter at least one positive payment allocation.");
      return;
    }
    setStatus("Recording the verified payment and queuing its receipt…");
    const response = await apiRequest("/api/payments/confirm", {
      body: JSON.stringify({
        allocations,
        amountCents,
        payerName: paymentPayer.trim(),
        propertyId,
        receivedAt: receivedAt.toISOString(),
        tenancyId: paymentTenancyId,
      }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The payment was not verified. No receipt was created.");
      return;
    }
    setPaymentFirstMonth("");
    setPaymentLastMonth("");
    setPaymentPayer("");
    setPaymentReceivedAt("");
    setPaymentRent("");
    setPaymentRentPeriodEnd("");
    setPaymentRentPeriodStart("");
    setPaymentSecurityDeposit("");
    setStatus("The payment was verified and its receipt job was queued.");
    await load();
  }

  async function reconcileLedger(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (ledgerEntryId === "") {
      setStatus("Select a ledger entry to reconcile.");
      return;
    }
    setStatus("Recording the reconciliation status…");
    const response = await apiRequest(
      `/api/ledger/${encodeURIComponent(ledgerEntryId)}/reconcile`,
      {
        body: JSON.stringify({ status: ledgerStatus }),
        headers: {
          "Content-Type": "application/json",
          "x-idempotency-key": crypto.randomUUID(),
        },
        method: "POST",
      },
    );
    if (!response.ok) {
      setStatus("The reconciliation status was not changed.");
      return;
    }
    setStatus("The reconciliation status was recorded.");
    await load();
  }

  async function manageAuthorizedUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
        userGrantTargetId,
      )
    ) {
      setStatus("Enter the existing authenticated user's valid account UUID.");
      return;
    }
    setStatus(`${userGrantAction === "grant" ? "Granting" : "Revoking"} authorized access…`);
    const response = await apiRequest("/api/access/authorized-users", {
      body: JSON.stringify({
        action: userGrantAction,
        canManageAuthorizedUsers: userGrantCanManage,
        propertyId: userGrantPropertyId || null,
        targetUserId: userGrantTargetId,
      }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("Authorized-user access was not changed.");
      return;
    }
    setUserGrantTargetId("");
    setStatus("The authorized-user change was recorded with an audit event.");
    await load();
  }

  async function downloadAccountingPdf() {
    if (pendingAction !== null) {
      return;
    }
    setPendingAction("accounting:pdf");
    setStatus("Preparing the authorized accounting PDF…");
    try {
      const response = await apiRequest("/api/accounting/export.pdf");
      if (!response.ok) {
        setStatus("The accounting PDF is temporarily unavailable.");
        return;
      }
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `potimus-accounting-${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(blobUrl);
      setStatus("Accounting PDF downloaded.");
    } catch {
      setStatus("The accounting PDF is temporarily unavailable.");
    } finally {
      setPendingAction(null);
    }
  }

  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      documentFile === null ||
      documentPropertyId === "" ||
      (documentKind === "signed_lease" && documentTenancyId === "")
    ) {
      setStatus("Select the document, property, and required tenancy.");
      return;
    }
    const form = new FormData();
    form.set("file", documentFile);
    form.set("propertyId", documentPropertyId);
    form.set("tenancyId", documentTenancyId);
    form.set("title", documentTitle);
    form.set("kind", documentKind);
    form.set(
      "audience",
      documentKind === "signed_lease"
        ? "tenant_and_accountant"
        : documentKind === "vendor_invoice"
          ? "accountant"
          : "internal",
    );
    setStatus("Encrypting, saving, and replicating the document…");
    const response = await apiRequest("/api/documents", {
      body: form,
      headers: { "x-idempotency-key": crypto.randomUUID() },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("The document was not accepted. No unverified record was activated.");
      return;
    }
    setDocumentFile(null);
    setDocumentTitle("");
    setStatus(
      documentKind === "signed_lease"
        ? "Signed lease saved. Extracted terms will remain inactive until confirmed."
        : "Document saved and replicated.",
    );
    await load();
  }

  async function confirmLease(leaseId: string, termVersionId: string) {
    setStatus("Recording the lease-term confirmation…");
    const response = await apiRequest(`/api/leases/${encodeURIComponent(leaseId)}/confirm`, {
      body: JSON.stringify({ termVersionId }),
      headers: {
        "Content-Type": "application/json",
        "x-idempotency-key": crypto.randomUUID(),
      },
      method: "POST",
    });
    if (!response.ok) {
      setStatus("Terms could not be confirmed. Missing or conflicting fields must be corrected.");
      return;
    }
    setStatus("Lease terms confirmed and activated with an audit record.");
    await load();
  }

  const isOperator = dashboard?.role === "owner" || dashboard?.role === "authorized_user";
  const canManageAuthorizedUsers =
    dashboard?.role === "owner" ||
    (dashboard?.role === "authorized_user" &&
      session?.scopes.some((scope) => scope.canManageAuthorizedUsers));
  const accessBoundary = dashboard === null ? null : roleBoundary(dashboard.role);

  return (
    <section aria-busy={loading} className="portal-page">
      <header className="portal-heading">
        <div>
          <p className="eyebrow">Private application</p>
          <h1>
            {session === null ? "Your records" : `${session.role.replaceAll("_", " ")} portal`}
          </h1>
        </div>
        <button className="button button-small" onClick={() => void signOut()} type="button">
          Sign out
        </button>
      </header>

      {session !== null && !session.mfa.compliant ? (
        <div className="security-banner" role="alert">
          <div>
            <strong>Security verification required</strong>
            <p>
              {session.mfa.verifiedFactors} of {session.mfa.requiredFactors} required factors are
              enrolled.
            </p>
          </div>
          <Link className="button button-small" to="/mfa">
            Verify or enroll
          </Link>
        </div>
      ) : null}
      <p aria-live="polite" className="portal-status">
        {status}
      </p>

      {accessBoundary === null ? null : (
        <section aria-labelledby="access-boundary-title" className="access-boundary">
          <h2 id="access-boundary-title">{accessBoundary.heading}</h2>
          <p>{accessBoundary.summary}</p>
        </section>
      )}

      {dashboard?.role === "tenant" ? (
        <div className="portal-grid">
          <section className="portal-panel">
            <h2>Communication preference</h2>
            <p>Choose the active channel for routine messages and payment receipts.</p>
            <form
              className="maintenance-form"
              onSubmit={(event) => void saveCommunicationPreference(event)}
            >
              <label htmlFor="preferred-channel">Preferred channel</label>
              <select
                aria-describedby="communication-preference-note"
                id="preferred-channel"
                onChange={(event) =>
                  setPreferredChannel(event.target.value === "sms" ? "sms" : "email")
                }
                value={preferredChannel}
              >
                <option value="email">Email</option>
                <option disabled value="sms">
                  Text message (separate consent and activation required)
                </option>
              </select>
              <p className="form-note" id="communication-preference-note">
                Saving a channel preference does not grant SMS consent. Text delivery stays disabled
                until separate consent is recorded and messaging is activated. Review the{" "}
                <Link to="/messaging">messaging program</Link> or continue using email.
              </p>
              <button className="button" disabled={preferredChannel === "sms"} type="submit">
                Save preference
              </button>
            </form>
          </section>
          <section className="portal-panel portal-panel-wide">
            <h2>Submit maintenance</h2>
            <p>
              Describe the issue. You can return here to follow it through to confirmed resolution.
            </p>
            <aside className="safety-notice" aria-labelledby="maintenance-safety-title">
              <p id="maintenance-safety-title">
                <strong>Emergency safety</strong>
              </p>
              <p>
                For fire, a gas odor, immediate danger, water near electricity, or a suspected
                break-in, call 911 or the appropriate utility emergency service first. This portal
                does not replace emergency services.
              </p>
            </aside>
            <form className="maintenance-form" onSubmit={(event) => void submitMaintenance(event)}>
              <label htmlFor="maintenance-title">Short title</label>
              <input
                id="maintenance-title"
                maxLength={120}
                onChange={(event) => setTitle(event.target.value)}
                required
                value={title}
              />
              <label htmlFor="maintenance-description">What is happening?</label>
              <textarea
                id="maintenance-description"
                maxLength={5000}
                onChange={(event) => setDescription(event.target.value)}
                required
                rows={5}
                value={description}
              />
              <label htmlFor="maintenance-urgency">Urgency</label>
              <select
                id="maintenance-urgency"
                onChange={(event) =>
                  setUrgency(event.target.value as "emergency" | "routine" | "urgent")
                }
                value={urgency}
              >
                <option value="routine">Routine</option>
                <option value="urgent">Urgent</option>
                <option value="emergency">Possible emergency</option>
              </select>
              <button className="button" type="submit">
                Submit request
              </button>
            </form>
          </section>
          <RecordSection
            empty="No maintenance cases are open."
            records={dashboard.maintenanceCases}
            title="Maintenance cases"
          />
          <RecordSection
            empty="No payments are recorded."
            records={dashboard.payments}
            title="Payments"
          />
          <RecordSection
            empty="No receipts are recorded."
            records={dashboard.receipts}
            title="Receipts"
          />
          <RecordSection
            empty="No tenant-visible communications are recorded."
            records={dashboard.communications}
            title="Communications"
          />
          {dashboard.maintenanceCases?.length ? (
            <section className="portal-panel">
              <h2>Add a private attachment</h2>
              <p>Photos and short videos stay private and are never used as marketing media.</p>
              <form
                className="maintenance-form"
                onSubmit={(event) => void uploadMaintenanceMedia(event)}
              >
                <label htmlFor="maintenance-media-case">Maintenance case</label>
                <select
                  id="maintenance-media-case"
                  onChange={(event) => setMaintenanceMediaCaseId(event.target.value)}
                  required
                  value={maintenanceMediaCaseId}
                >
                  <option value="">Select a case</option>
                  {dashboard.maintenanceCases.map((maintenanceCase) => (
                    <option
                      key={String(maintenanceCase["id"])}
                      value={String(maintenanceCase["id"])}
                    >
                      {displayValue(maintenanceCase, ["case_number", "title"])}
                    </option>
                  ))}
                </select>
                <label htmlFor="maintenance-media-file">Photo or short video</label>
                <input
                  accept="image/jpeg,image/png,image/webp,video/mp4,video/quicktime"
                  id="maintenance-media-file"
                  onChange={(event) => setMaintenanceMediaFile(event.target.files?.[0] ?? null)}
                  required
                  type="file"
                />
                <button className="button" type="submit">
                  Save private attachment
                </button>
              </form>
            </section>
          ) : null}
          {dashboard.maintenanceCases?.some(
            (maintenanceCase) => maintenanceCase["status"] === "awaiting_confirmation",
          ) ? (
            <section className="portal-panel">
              <h2>Confirm completed work</h2>
              <p>A case remains open until you or an authorized user confirms completion.</p>
              <ul className="record-list">
                {dashboard.maintenanceCases
                  .filter(
                    (maintenanceCase) => maintenanceCase["status"] === "awaiting_confirmation",
                  )
                  .map((maintenanceCase) => (
                    <li key={String(maintenanceCase["id"])}>
                      <strong>{displayValue(maintenanceCase, ["title", "case_number"])}</strong>
                      <button
                        className="text-button"
                        onClick={() => void confirmClosure(String(maintenanceCase["id"]))}
                        type="button"
                      >
                        Confirm work is complete
                      </button>
                    </li>
                  ))}
              </ul>
            </section>
          ) : null}
          <DocumentSection
            documents={dashboard.documents}
            empty="No tenant-facing documents are available."
            onDownload={downloadDocument}
            pendingAction={pendingAction}
            title="Your documents"
          />
        </div>
      ) : null}

      {dashboard !== null && dashboard.role !== "tenant" ? (
        <div className="portal-grid">
          {canManageAuthorizedUsers ? (
            <section className="portal-panel portal-panel-wide">
              <h2>Authorized-user access</h2>
              <p>
                Grant or revoke operational access for an existing authenticated account. The sole
                owner cannot be changed here.
              </p>
              <form
                className="maintenance-form"
                onSubmit={(event) => void manageAuthorizedUser(event)}
              >
                <label htmlFor="authorized-user-id">Target account UUID</label>
                <input
                  id="authorized-user-id"
                  onChange={(event) => setUserGrantTargetId(event.target.value.trim())}
                  required
                  value={userGrantTargetId}
                />
                <label htmlFor="authorized-user-action">Action</label>
                <select
                  id="authorized-user-action"
                  onChange={(event) =>
                    setUserGrantAction(event.target.value === "revoke" ? "revoke" : "grant")
                  }
                  value={userGrantAction}
                >
                  <option value="grant">Grant authorized-user access</option>
                  <option value="revoke">Revoke authorized-user access</option>
                </select>
                <label htmlFor="authorized-user-property">Property scope</label>
                <select
                  id="authorized-user-property"
                  onChange={(event) => setUserGrantPropertyId(event.target.value)}
                  value={userGrantPropertyId}
                >
                  <option value="">All properties</option>
                  {dashboard.properties?.map((property) => (
                    <option key={String(property["id"])} value={String(property["id"])}>
                      {displayValue(property, ["legal_name"])}
                    </option>
                  ))}
                </select>
                {dashboard.role === "owner" ? (
                  <label className="checkbox-label">
                    <input
                      checked={userGrantCanManage}
                      onChange={(event) => setUserGrantCanManage(event.target.checked)}
                      type="checkbox"
                    />{" "}
                    Allow this user to manage other authorized users
                  </label>
                ) : null}
                <button className="button" type="submit">
                  Record access change
                </button>
              </form>
            </section>
          ) : null}
          {isOperator ? (
            <section className="portal-panel portal-panel-wide">
              <h2>Save paperwork</h2>
              <p>
                Signed leases are tenant-facing and always require confirmation before extracted
                terms become active. Every file is copied to backup storage.
              </p>
              <form className="maintenance-form" onSubmit={(event) => void uploadDocument(event)}>
                <label htmlFor="document-title">Document title</label>
                <input
                  id="document-title"
                  maxLength={180}
                  onChange={(event) => setDocumentTitle(event.target.value)}
                  required
                  value={documentTitle}
                />
                <label htmlFor="document-kind">Document type</label>
                <select
                  id="document-kind"
                  onChange={(event) =>
                    setDocumentKind(
                      event.target.value as "other" | "signed_lease" | "vendor_invoice",
                    )
                  }
                  value={documentKind}
                >
                  <option value="signed_lease">Signed lease</option>
                  <option value="vendor_invoice">Vendor invoice</option>
                  <option value="other">Internal paperwork</option>
                </select>
                <label htmlFor="document-property">Property</label>
                <select
                  id="document-property"
                  onChange={(event) => setDocumentPropertyId(event.target.value)}
                  required
                  value={documentPropertyId}
                >
                  <option value="">Select property</option>
                  {dashboard.properties?.map((property) => (
                    <option key={String(property["id"])} value={String(property["id"])}>
                      {displayValue(property, ["legal_name"])}
                    </option>
                  ))}
                </select>
                <label htmlFor="document-tenancy">
                  Tenancy {documentKind === "signed_lease" ? "(required)" : "(optional)"}
                </label>
                <select
                  id="document-tenancy"
                  onChange={(event) => setDocumentTenancyId(event.target.value)}
                  required={documentKind === "signed_lease"}
                  value={documentTenancyId}
                >
                  <option value="">No tenancy</option>
                  {dashboard.tenancies?.map((tenancy) => (
                    <option key={String(tenancy["id"])} value={String(tenancy["id"])}>
                      {String(tenancy["id"])}
                    </option>
                  ))}
                </select>
                <label htmlFor="document-file">File</label>
                <input
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.txt"
                  id="document-file"
                  onChange={(event) => setDocumentFile(event.target.files?.[0] ?? null)}
                  required
                  type="file"
                />
                <button className="button" disabled={!dashboard.properties?.length} type="submit">
                  Save and replicate
                </button>
                {dashboard.properties?.length ? null : (
                  <p className="form-note">
                    No authorized property is available for document upload.
                  </p>
                )}
              </form>
            </section>
          ) : null}
          {isOperator ? (
            <section className="portal-panel portal-panel-wide">
              <h2>Confirm a tenant payment</h2>
              <p>
                Use this only after verifying Zelle evidence. The system records the payment and
                creates a receipt; it never moves funds.
              </p>
              <form
                className="maintenance-form"
                onSubmit={(event) => void confirmManualPayment(event)}
              >
                <label htmlFor="payment-tenancy">Tenancy</label>
                <select
                  id="payment-tenancy"
                  onChange={(event) => setPaymentTenancyId(event.target.value)}
                  required
                  value={paymentTenancyId}
                >
                  <option value="">Select tenancy</option>
                  {dashboard.tenancies?.map((tenancy) => (
                    <option key={String(tenancy["id"])} value={String(tenancy["id"])}>
                      {String(tenancy["id"])}
                    </option>
                  ))}
                </select>
                <label htmlFor="payment-payer">Payer name</label>
                <input
                  id="payment-payer"
                  maxLength={180}
                  onChange={(event) => setPaymentPayer(event.target.value)}
                  required
                  value={paymentPayer}
                />
                <label htmlFor="payment-received-at">Received date and time</label>
                <input
                  id="payment-received-at"
                  onChange={(event) => setPaymentReceivedAt(event.target.value)}
                  required
                  type="datetime-local"
                  value={paymentReceivedAt}
                />
                <label htmlFor="payment-deposit">Security deposit allocation (USD)</label>
                <input
                  id="payment-deposit"
                  inputMode="decimal"
                  min="0"
                  onChange={(event) => setPaymentSecurityDeposit(event.target.value)}
                  step="0.01"
                  type="number"
                  value={paymentSecurityDeposit}
                />
                <label htmlFor="payment-first-month">First-month allocation (USD)</label>
                <input
                  id="payment-first-month"
                  inputMode="decimal"
                  min="0"
                  onChange={(event) => setPaymentFirstMonth(event.target.value)}
                  step="0.01"
                  type="number"
                  value={paymentFirstMonth}
                />
                <label htmlFor="payment-last-month">Last-month prepaid allocation (USD)</label>
                <input
                  id="payment-last-month"
                  inputMode="decimal"
                  min="0"
                  onChange={(event) => setPaymentLastMonth(event.target.value)}
                  step="0.01"
                  type="number"
                  value={paymentLastMonth}
                />
                <label htmlFor="payment-rent">Ordinary rent allocation (USD)</label>
                <input
                  id="payment-rent"
                  inputMode="decimal"
                  min="0"
                  onChange={(event) => setPaymentRent(event.target.value)}
                  step="0.01"
                  type="number"
                  value={paymentRent}
                />
                <label htmlFor="payment-period-start">Rent period start</label>
                <input
                  id="payment-period-start"
                  onChange={(event) => setPaymentRentPeriodStart(event.target.value)}
                  type="date"
                  value={paymentRentPeriodStart}
                />
                <label htmlFor="payment-period-end">Rent period end</label>
                <input
                  id="payment-period-end"
                  onChange={(event) => setPaymentRentPeriodEnd(event.target.value)}
                  type="date"
                  value={paymentRentPeriodEnd}
                />
                <button className="button" disabled={!dashboard.tenancies?.length} type="submit">
                  Verify and create receipt
                </button>
                {dashboard.tenancies?.length ? null : (
                  <p className="form-note">
                    Payment confirmation is unavailable until an authorized tenancy is present.
                  </p>
                )}
              </form>
            </section>
          ) : null}
          {isOperator ? (
            <section className="portal-panel portal-panel-wide">
              <h2>Lease terms awaiting confirmation</h2>
              {dashboard.leases?.some((lease) => lease["status"] === "awaiting_confirmation") ? (
                <ul className="record-list">
                  {dashboard.leases
                    .filter((lease) => lease["status"] === "awaiting_confirmation")
                    .map((lease) => {
                      const terms = dashboard.leaseTermVersions?.find(
                        (version) => version["lease_id"] === lease["id"],
                      );
                      return (
                        <li key={String(lease["id"])}>
                          <strong>Lease {String(lease["id"])}</strong>
                          <span>
                            Rent cents: {scalarValue(terms?.["rent_cents"])}; due day:{" "}
                            {scalarValue(terms?.["due_day"])}; dates:{" "}
                            {scalarValue(terms?.["starts_on"])} to {scalarValue(terms?.["ends_on"])}
                          </span>
                          {typeof terms?.["id"] === "string" ? (
                            <button
                              className="text-button"
                              onClick={() =>
                                void confirmLease(String(lease["id"]), String(terms["id"]))
                              }
                              type="button"
                            >
                              Confirm these terms
                            </button>
                          ) : (
                            <button className="text-button" disabled type="button">
                              Awaiting extracted terms
                            </button>
                          )}
                        </li>
                      );
                    })}
                </ul>
              ) : (
                <p>No lease terms await confirmation.</p>
              )}
            </section>
          ) : null}
          <section className="portal-panel portal-panel-wide">
            <h2>Authorized records</h2>
            <p>
              Financial exports contain only records allowed for your role. Communications and
              recordings are excluded from accountant access.
            </p>
            <button
              className="button button-small"
              disabled={pendingAction !== null}
              onClick={() => void downloadAccountingExport()}
              type="button"
            >
              {pendingAction === "accounting:csv"
                ? "Preparing accounting CSV…"
                : "Download accounting CSV"}
            </button>
            <button
              className="text-button"
              disabled={pendingAction !== null}
              onClick={() => void downloadAccountingPdf()}
              type="button"
            >
              {pendingAction === "accounting:pdf"
                ? "Preparing accounting PDF…"
                : "Download accounting PDF"}
            </button>
          </section>
          <section className="portal-panel portal-panel-wide">
            <h2>Ledger review</h2>
            {dashboard.ledgerEntries?.length ? (
              <>
                <label htmlFor="ledger-entry">Ledger entry</label>
                <select
                  className="field-control"
                  id="ledger-entry"
                  onChange={(event) => setLedgerEntryId(event.target.value)}
                  value={ledgerEntryId}
                >
                  <option value="">Select an entry</option>
                  {dashboard.ledgerEntries.map((entry) => (
                    <option key={String(entry["id"])} value={String(entry["id"])}>
                      {displayValue(entry, ["category", "entry_type"])} · {recordDetail(entry)}
                    </option>
                  ))}
                </select>
                <form
                  className="maintenance-form"
                  onSubmit={(event) => void correctLedgerCategory(event)}
                >
                  <label htmlFor="ledger-category">Correct category</label>
                  <input
                    id="ledger-category"
                    maxLength={120}
                    onChange={(event) => setLedgerCategory(event.target.value)}
                    required
                    value={ledgerCategory}
                  />
                  <label htmlFor="ledger-account-code">Account code</label>
                  <input
                    id="ledger-account-code"
                    maxLength={60}
                    onChange={(event) => setLedgerAccountCode(event.target.value)}
                    required
                    value={ledgerAccountCode}
                  />
                  <button className="button button-small" type="submit">
                    Save audited correction
                  </button>
                </form>
                <form
                  className="maintenance-form"
                  onSubmit={(event) => void reconcileLedger(event)}
                >
                  <label htmlFor="ledger-status">Reconciliation status</label>
                  <select
                    id="ledger-status"
                    onChange={(event) =>
                      setLedgerStatus(event.target.value as "exception" | "matched" | "reviewed")
                    }
                    value={ledgerStatus}
                  >
                    <option value="matched">Matched</option>
                    <option value="exception">Exception</option>
                    <option value="reviewed">Reviewed</option>
                  </select>
                  <button className="button button-small" type="submit">
                    Save reconciliation
                  </button>
                </form>
              </>
            ) : (
              <p>No ledger entries are available for review.</p>
            )}
          </section>
          <RecordSection empty="No properties." records={dashboard.properties} title="Properties" />
          {isOperator ? (
            <>
              <RecordSection
                empty="No open action items."
                records={dashboard.actionItems}
                title="Action items"
              />
              <RecordSection
                empty="No maintenance cases."
                records={dashboard.maintenanceCases}
                title="Maintenance"
              />
              <RecordSection empty="No leases." records={dashboard.leases} title="Leases" />
              <RecordSection
                empty="No approvals."
                records={dashboard.approvals}
                title="Approvals"
              />
            </>
          ) : null}
          <RecordSection empty="No payments." records={dashboard.payments} title="Payments" />
          <RecordSection empty="No receipts." records={dashboard.receipts} title="Receipts" />
          <RecordSection
            empty="No vendor payment packets."
            records={dashboard.paymentPackets}
            title="Vendor payment packets"
          />
          {canManageAuthorizedUsers ? (
            <RecordSection
              empty="No authorized-user grants."
              records={dashboard.roleGrants}
              title="Access grants"
            />
          ) : null}
          {dashboard.role === "owner" ? (
            <>
              <CommunicationTimingSection
                attempts={dashboard.deliveryAttempts}
                communications={dashboard.communicationTimings}
              />
              <RecordSection
                empty="No provider incidents."
                records={dashboard.providerHealth}
                title="Provider health"
              />
              <RecordSection
                empty="No backup exports recorded."
                records={dashboard.backupExports}
                title="Backup exports"
              />
              <RecordSection
                empty="No restore drills recorded."
                records={dashboard.restoreDrills}
                title="Restore drills"
              />
              <RecordSection
                empty="No cost forecasts recorded."
                records={dashboard.costForecasts}
                title="Cost forecasts"
              />
            </>
          ) : null}
          {isOperator ? (
            <>
              <RecordSection
                empty="No active escalations."
                records={dashboard.escalations}
                title="Escalations"
              />
              <RecordSection
                empty="No work orders."
                records={dashboard.workOrders}
                title="Work orders"
              />
              <RecordSection empty="No vendors." records={dashboard.vendors} title="Vendors" />
            </>
          ) : null}
          <RecordSection
            empty="No ledger entries."
            records={dashboard.ledgerEntries}
            title="Ledger"
          />
          <DocumentSection
            documents={dashboard.documents}
            empty="No documents are available for your role."
            onDownload={downloadDocument}
            pendingAction={pendingAction}
            title="Documents"
          />
        </div>
      ) : null}
    </section>
  );
}
